import {Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {DateTime, TimeInterval} from '../../../../core/models/order.model';
import {PickUpDateTimeComponent} from '../pick-up-date-time/pick-up-date-time.component';
import {PopoverController} from '@ionic/angular';
import { Restaurant, WorkDay } from '../../../../core/models/restaurant.model';
import {DatePipe, LowerCasePipe} from '@angular/common';
import {CartStore} from '../../../../core/stores/cart.store';
import {RestaurantStore} from '../../../../core/stores/restaurant.store';
import * as moment from 'moment';
import {FormatDataService} from '../../../../core/services/format-data.service';
import { ActivatedRoute } from '@angular/router';
import { TimeService } from '../../../../core/services/time.service';

@Component({
  selector: 'app-scheduled-pickup-timepicker',
  templateUrl: './scheduled-pickup-timepicker.component.html',
  styleUrls: ['./scheduled-pickup-timepicker.component.scss']
})
export class ScheduledPickupTimepickerComponent implements OnInit, OnChanges {
  @Input() hoursOfOperation: WorkDay[];
  @Input() acceptOrders: boolean;
  @Input() forceTouched;
  @Input() pickupDaysInAdvance;
  @Input() disableASAP: boolean;
  @Input() selectASAP: boolean;
  @Input() estimatedTimeInMinutes: number;
  @Input() hideETA: boolean;
  @Input() hidePickupTime: boolean = false;
  @Input() showWindowEndTime: boolean;
  @Input() useFlavorShifts: boolean = false;
  @Input() shifts:any[] = [];
  @Output() validChange: EventEmitter<boolean> = new EventEmitter<boolean>(false);
  @ViewChild('button', {static: false, read: ElementRef}) button: ElementRef;
  public dateTime: DateTime;
  public isDateTimeModalOpen = false;
  public asap: boolean;
  public isShowOnlyDay: boolean;
  public touched = false;
  public valid = false;
  private locationId: string;
  private selectSoonest: boolean;

  private get interval(): number {
    return Number(moment.duration(this.cartStore.pickupWindowDuration).asMinutes());
  }

  constructor(
    public popoverController: PopoverController,
    private lowerCasePipe: LowerCasePipe,
    private datePipe: DatePipe,
    private cartStore: CartStore,
    private formatDataService: FormatDataService,
    private restaurantStore: RestaurantStore,
    private route: ActivatedRoute,
    private timeService: TimeService
  ) {
  }

  ngOnInit() {
    this.getLocationId();
    this.initData();
    this.getRestaurant();
    this.setDataToService();
    this.setFirstDate();
  }

  ngOnChanges({forceTouched}: SimpleChanges): void {
    if (!forceTouched) {
      return;
    }
    const {previousValue: previousForceTouched, currentValue: currentForceTouched} = forceTouched;
    if (previousForceTouched !== currentForceTouched && currentForceTouched) {
      this.handleValidate();
    }
  }

  private initData(): void {
    if (this.acceptOrders && !this.restaurantStore.disableTodayOrders && this.isTimeRangeStartFromToday()) {
      if (this.selectASAP || this.cartStore.pickupAt) {
        this.dateTime = new DateTime(new Date());
      }
      if (this.selectASAP) {
        this.cartStore.isAsap = true;
        this.cartStore.pickupAt = new Date().toISOString();
      }
      if (!this.disableASAP) {
        this.asap = true;
      }
    } if (this.restaurantStore.isKioskSmsOrder && this.restaurantStore.kioskSmsOrderData) {
      const kioskOrderPickupTime = this.restaurantStore.kioskSmsOrderData.pickupAt;
      this.cartStore.isAsap = this.restaurantStore.kioskSmsOrderData.isAsap;
      this.cartStore.pickupAt = kioskOrderPickupTime.toISOString();
      this.asap = this.restaurantStore.kioskSmsOrderData.isAsap;
      this.dateTime = new DateTime(kioskOrderPickupTime,
        new TimeInterval(kioskOrderPickupTime, moment(kioskOrderPickupTime).add(this.interval, 'm').toDate()));
    }
  }

  private handleValidate() {
    this.touched = true;
    const valid = !!this.dateTime;
    if (valid !== this.valid) {
      this.validChange.emit(valid);
    }
    this.valid = valid;
  }

  async pickUpDateTime() {
    const clientRect = this.button.nativeElement.getBoundingClientRect();
    const event: any = { target: {getBoundingClientRect: () => clientRect} };
    const daysInAdvance = this.restaurantStore.timeRangeParams != null ? this.formatDataService.daysCount(new Date(), this.restaurantStore.timeRangeParams.endTime) + 2 : this.pickupDaysInAdvance;
    const initialDate = this.restaurantStore.timeRangeParams != null ? this.hoursOfOperation[0].date : this.dateTime && this.dateTime.date;
    const popover = await this.popoverController.create({
      component: PickUpDateTimeComponent,
      componentProps: {
        hoursOfOperation: this.hoursOfOperation,
        initialDate: initialDate,
        initialTimeInterval: this.dateTime && this.dateTime.timeInterval,
        interval: this.interval,
        asap: this.asap,
        pickupDaysInAdvance: daysInAdvance,
        estimatedTimeInMinutes: this.estimatedTimeInMinutes,
        hideETA: this.hideETA,
        disableASAP: this.disableASAP,
        selectASAP: this.selectASAP,
        acceptingOrders: this.acceptOrders,
        timezone: this.restaurantStore.timezone,
        disableTodayOrders: this.restaurantStore.disableTodayOrders,
        showWindowEndTime: this.showWindowEndTime,
        estimateDuration: this.cartStore.orderMap[this.locationId]?.estimatedDurationDefault,
        useFlavorShifts: this.useFlavorShifts,
        shifts: this.shifts
      },
      showBackdrop: false,
      event: event,
      cssClass: 'select-date-time-popover',
    });

    this.isDateTimeModalOpen = true;

    popover.onDidDismiss().then(({data}) => {
      this.isDateTimeModalOpen = false;
      if (data) {
        const {dateTime, asap, fullDayPickUpAvailable} = data;
        this.dateTime = dateTime;
        this.asap = asap;
        this.cartStore.pickupAt = this.asap ? new Date().toISOString() : this.dateTime.timeInterval.start.toISOString();
        this.cartStore.isAsap = asap;
        this.isShowOnlyDay = fullDayPickUpAvailable;
        this.handleValidate();
      }
    });

    return await popover.present().then(() => {
      setTimeout(() => {
        const backgrounds = document.getElementsByTagName('ion-backdrop');
        if (backgrounds.length === 1) {
          backgrounds[0].addEventListener('click', function () {
            popover.dismiss();
          });
        }
      }, 300);
    });
  }

  public get noDateTimeSelectedText(): string {
    if (this.interval < 60 * 24) {
      return 'Select pickup time';
    } else {
      return 'Select pickup day';
    }
  }

  public get errorMessageText(): string {
    if (this.interval < 60 * 24) {
      return 'Please select a pickup time';
    } else {
      return 'Please select a pickup day';
    }
  }

  public formatDateTime({date, timeInterval}: DateTime): string {
    const datePart = this.isToday(date) ? 'Today' : this.datePipe.transform(date, 'EEE, MMM d');
    if (this.isShowOnlyDay) {
      return `${datePart}`;
    }
    const timePart = this.formatTimePart(timeInterval, this.asap);
    return timePart ? `${datePart} - ${timePart}` : datePart;
  }

  public formatTimePart(timeInterval: TimeInterval, asap = false) {
    if (asap && this.hasAsap() && !this.useFlavorShifts) {
      if (this.cartStore.orderMap[this.locationId]?.estimatedDuration) {
        return `ASAP (~${this.cartStore.orderMap[this.locationId]?.estimatedDuration})`;
      }

      return this.hideETA ? 'ASAP' : `ASAP (~${Math.ceil(Number(this.estimatedTimeInMinutes))} min)`;
    }
    if (this.selectSoonest && timeInterval) {
      return this.showWindowEndTime ? `${this.formatTime(timeInterval.start)} - ${this.formatTime(timeInterval.end)}` : this.formatTime(timeInterval.start);
    }
    if (!timeInterval) {
      return;
    }
    const {start, end} = timeInterval;
    return this.showWindowEndTime ? `${this.formatTime(start)} - ${this.formatTime(end)}` : this.formatTime(start);
  }

  private isToday(date: Date): boolean {
    if (!date) {
      return;
    }
    const today = new Date();
    return today.toDateString() === date.toDateString();
  }

  private isTimeRangeStartFromToday(): boolean {
    if (this.restaurantStore.timeRangeParams != null) {
      const today = new Date();
      return this.formatDataService.isSameDate(this.restaurantStore.timeRangeParams.startTime, today) ||
        (this.formatDataService.isAfterDate(today, this.restaurantStore.timeRangeParams.startTime) &&
         this.formatDataService.isAfterDate(today, this.restaurantStore.timeRangeParams.endTime));
    }
    return true;
  }

  private formatTime(time: Date): string {
    return this.lowerCasePipe.transform(this.datePipe.transform(time, 'h:mma'));
  }

  private getRestaurant(): void {
    this.restaurantStore.getRestaurantById(this.locationId)
      .subscribe((restaurant: Restaurant) => {
        if (restaurant) {
          this.selectSoonest = restaurant.select_soonest_available_by_default;
        }
      });
  }

  private getLocationId(): void {
    this.route.params.subscribe((params) => {
      this.locationId = params.locationId;
    });
  }
  private setDataToService(): void {
    const initialDate = this.restaurantStore.timeRangeParams != null ? this.hoursOfOperation[0].date : this.dateTime && this.dateTime.date;
    const daysInAdvance = this.restaurantStore.timeRangeParams != null ? this.formatDataService.daysCount(new Date(), this.restaurantStore.timeRangeParams.endTime) + 2 : this.pickupDaysInAdvance;
    this.timeService.hoursOfOperation = this.hoursOfOperation;
    this.timeService.initialDate = initialDate;
    this.timeService.initialTimeInterval = this.dateTime && this.dateTime.timeInterval;
    this.timeService.interval = this.interval;
    this.timeService.asap = this.asap;
    this.timeService.pickupDaysInAdvance = daysInAdvance;
    this.timeService.estimatedTimeInMinutes = this.estimatedTimeInMinutes;
    this.timeService.hideETA = this.hideETA;
    this.timeService.disableASAP = this.disableASAP;
    this.timeService.selectASAP = this.selectASAP;
    this.timeService.acceptingOrders = this.acceptOrders;
    this.timeService.timezone = this.restaurantStore.timezone;
    this.timeService.disableTodayOrders = this.restaurantStore.disableTodayOrders;
    this.timeService.showWindowEndTime = this.showWindowEndTime;
  }
  private setFirstDate(): void {
    let timeInterval;
    if(this.useFlavorShifts){
      timeInterval = this.timeService.generateTimeIntervalsCF(this.shifts, this.interval)
    } else {
      timeInterval = this.timeService.generateIntervals();
    }
    if (timeInterval && timeInterval.length > 0) {
      if(this.useFlavorShifts){
        this.dateTime = new DateTime(
          timeInterval[0].end,
          new TimeInterval(
            timeInterval[0].start,
            timeInterval[0].end,
          ));
          this.cartStore.pickupAt = this.dateTime.timeInterval.start.toISOString()
          this.cartStore.isAsap = this.asap ? this.asap : false
      }
      else if (this.selectSoonest && (!this.asap && !this.hasAsap())) {
        this.dateTime = new DateTime(
          timeInterval[0].end,
          new TimeInterval(
            timeInterval[0].start,
            timeInterval[0].end,
          ));
          this.cartStore.pickupAt = this.dateTime.timeInterval.start.toISOString()
          this.cartStore.isAsap = this.asap ? this.asap : false
        }
    }
  }
  public hasAsap(): boolean {
    return this.timeService.hasAsap(
      this.timeService.initDate(
        this.timeService.nextWorkDaysInAdvance(null,1)
      )
    );
  }
}
